import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as RadixAlert from '@radix-ui/react-alert-dialog';
import { AlertTriangle } from 'lucide-react';
import { cn } from './cn';
import { Button } from './Button';

export interface ConfirmOptions {
  title: ReactNode;
  /** What will happen, in one or two sentences. */
  description?: ReactNode;
  /** The confirming button's text: a verb naming the action ("Refund", "Delete"), never "OK". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` (default) for destructive or money-moving actions, `primary` otherwise. */
  tone?: 'danger' | 'primary';
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  onOpenChange(open: boolean): void;
  /** Runs on confirm; while its promise is pending the button spins, then the dialog closes. */
  onConfirm(): void | Promise<unknown>;
  /** Blocks the confirm button (e.g. until a typed phrase matches). */
  confirmDisabled?: boolean;
  /** Extra content between the description and the buttons. */
  children?: ReactNode;
}

/**
 * Asks before an irreversible action. An alert dialog: no outside-click dismissal, Esc cancels,
 * and focus starts on Cancel so Enter never confirms by accident.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  onConfirm,
  confirmDisabled,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <RadixAlert.Root open={open} onOpenChange={onOpenChange}>
      <RadixAlert.Portal>
        <RadixAlert.Overlay className="fixed inset-0 z-50 bg-navy-ink/40" />
        <RadixAlert.Content className="fixed left-1/2 top-[18vh] z-50 w-[calc(100vw-2rem)] max-w-[440px] -translate-x-1/2 rounded-xl border border-line bg-surface p-5 font-sans text-ink shadow-pop outline-none">
          <div className="flex gap-3">
            <span
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-full',
                tone === 'danger' ? 'bg-danger-soft text-danger-ink' : 'bg-accent-soft text-accent',
              )}
              aria-hidden
            >
              <AlertTriangle size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <RadixAlert.Title className="m-0 font-display text-lg font-semibold text-heading">{title}</RadixAlert.Title>
              <RadixAlert.Description className={cn('m-0 mt-1 text-sm text-ink-muted', !description && 'sr-only')}>{description ?? title}</RadixAlert.Description>
              {children && <div className="mt-3">{children}</div>}
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <RadixAlert.Cancel asChild>
              <Button>{cancelLabel ?? t('actions.cancel')}</Button>
            </RadixAlert.Cancel>
            <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={busy} disabled={confirmDisabled} onClick={confirm}>
              {confirmLabel ?? t('actions.confirm')}
            </Button>
          </div>
        </RadixAlert.Content>
      </RadixAlert.Portal>
    </RadixAlert.Root>
  );
}

type Ask = (options: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<Ask | null>(null);

/** Hosts the dialog behind useConfirm(). Mounted once in UiProvider. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  const [open, setOpen] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const ask = useCallback<Ask>((options) => {
    resolver.current?.(false);
    setPending(options);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpen(false);
  };

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {pending && <ConfirmDialog {...pending} open={open} onOpenChange={(next) => !next && settle(false)} onConfirm={() => settle(true)} />}
    </ConfirmContext.Provider>
  );
}

/**
 * Promise-style confirmation for event handlers:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete this part?', confirmLabel: 'Delete' })) remove.mutate(id);
 */
export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext);
  if (!ask) throw new Error('useConfirm outside ConfirmProvider (UiProvider)');
  return ask;
}
